import React, { createContext, useState, useEffect, useCallback } from "react";
import {
    onAuthStateChanged,
    signInWithPopup,
    signOut,
    // NEW IMPORTS
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    sendEmailVerification
} from "firebase/auth";
import { auth, googleProvider } from "../config/firebaseConfig";
import apiClient from "../config/axiosConfig";
import {WorkspaceProvider} from "./WorkspaceContext";
import AccountStatusGate from "../components/AccountStatusGate";
import { cancelAccountDeletion, downloadAccountExport } from "../services/AccountService.mjs";

export const UserContext = createContext();

/**
 * fe-core-1: recovery screen shown when a user IS authenticated with Firebase but
 * the backend handshake (POST /api/auth/register) failed. Previously the failure
 * was only logged, leaving the user on the login screen with no way forward (the
 * Firebase session persists, so re-logging-in doesn't re-fire the handshake).
 * Now they can retry or sign out.
 */
const HandshakeErrorScreen = ({ onRetry, onSignOut, busy }) => (
    <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        padding: "var(--qd-space-6)", background: "var(--qd-bg)", color: "var(--qd-text)",
        fontFamily: "var(--qd-font-sans)", textAlign: "center"
    }}>
        <div style={{ maxWidth: 420 }}>
            <h2 style={{ margin: "0 0 8px" }}>Couldn’t reach the server</h2>
            <p style={{ opacity: 0.7, margin: "0 0 20px", lineHeight: 1.5 }}>
                You’re signed in, but we couldn’t finish connecting to QDex. This is
                usually temporary — please try again.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <button className="qd-btn qd-btn--primary" onClick={onRetry} disabled={busy}>
                    {busy ? "Retrying…" : "Try again"}
                </button>
                <button className="qd-btn" onClick={onSignOut} disabled={busy}>
                    Sign out
                </button>
            </div>
        </div>
    </div>
);

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [workspace, setWorkspace] = useState(null);
    const [loading, setLoading] = useState(true);
    // fe-core-1: track the raw Firebase user + handshake error so the handshake
    // can be retried without forcing the user to re-authenticate.
    const [firebaseUser, setFirebaseUser] = useState(null);
    const [authError, setAuthError] = useState(null);
    const [retrying, setRetrying] = useState(false);

    const runHandshake = useCallback(async () => {
        // The handshake works the same for Google OR Email.
        setAuthError(null);
        try {
            const res = await apiClient.post("/api/auth/register", null, {
                // The global toast + the on-screen recovery UI would double-report;
                // we render our own error state here.
                skipGlobalErrorToast: true,
            });
            setUser(res.data.user);
            setWorkspace(res.data.user.defaultWorkspaceId);
        } catch (error) {
            console.error("Backend Handshake Failed:", error);
            setUser(null);
            setWorkspace(null);
            setAuthError(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
            setFirebaseUser(fbUser);
            if (fbUser) {
                setLoading(true);
                await runHandshake();
            } else {
                setUser(null);
                setWorkspace(null);
                setAuthError(null);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [runHandshake]);

    const retryHandshake = useCallback(async () => {
        if (!firebaseUser) return;
        setRetrying(true);
        try {
            await runHandshake();
        } finally {
            setRetrying(false);
        }
    }, [firebaseUser, runHandshake]);

    const loginGoogle = () => {
        return signInWithPopup(auth, googleProvider);
    };

    // --- NEW FUNCTIONS ---
    const loginEmail = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const signupEmail = (email, password) => {
        return createUserWithEmailAndPassword(auth, email, password);
    };
    // ---------------------

    /**
     * M1 — password reset. Before this there was no way back into an
     * email/password account: no "forgot password" link existed anywhere, so a
     * forgotten password meant a permanently inaccessible account.
     *
     * Two-step by design. The backend is asked first, because it can send OUR
     * branded email and record a security event. It answers `fallback: true` when
     * it can't mint the link — most importantly when the runtime service account
     * lacks Firebase Auth permission, the same IAM gap that forced `checkRevoked`
     * off in authMiddleware. In that case we fall back to the Firebase client SDK,
     * which needs only the public web API key and therefore always works.
     *
     * Either way the caller sees the same generic outcome: whether an account
     * exists for the address is never revealed.
     */
    const resetPassword = useCallback(async (email) => {
        let needsFallback = true;
        try {
            const res = await apiClient.post(
                "/api/auth/password-reset",
                { email },
                { skipGlobalErrorToast: true },
            );
            needsFallback = res?.data?.fallback !== false;
        } catch {
            // The backend being unreachable is exactly when the client SDK matters.
            needsFallback = true;
        }

        if (needsFallback) {
            try {
                await sendPasswordResetEmail(auth, email);
            } catch (err) {
                // `auth/user-not-found` must NOT surface — it would turn this into
                // an account-existence oracle, which the backend path is careful to
                // avoid. Anything else is a genuine failure worth reporting.
                if (err?.code !== "auth/user-not-found" && err?.code !== "auth/invalid-email") {
                    throw err;
                }
            }
        }
    }, []);

    /**
     * M2 — resend the verification email. Same two-step shape as resetPassword:
     * prefer the branded backend send, fall back to the client SDK.
     */
    const resendVerification = useCallback(async () => {
        let needsFallback = true;
        try {
            const res = await apiClient.post("/api/auth/send-verification", null, {
                skipGlobalErrorToast: true,
            });
            needsFallback = res?.data?.fallback !== false;
        } catch {
            needsFallback = true;
        }

        if (needsFallback && auth.currentUser) {
            await sendEmailVerification(auth.currentUser);
        }
    }, []);

    const logout = () => {
        return signOut(auth);
    };

    /**
     * Restore a scheduled deletion, then re-run the handshake so the app opens
     * immediately rather than leaving the user on a screen that no longer applies.
     */
    const restoreAccount = useCallback(async () => {
        await cancelAccountDeletion();
        await runHandshake();
    }, [runHandshake]);

    // A non-active account is locked out of the whole API except the few
    // lifecycle routes, so rendering the app would just produce a wall of 403s.
    // The gate is the only usable surface — and it's where "restore" lives.
    const accountStatus = user?.status ?? 'active';

    // fe-core-1: an authenticated Firebase user whose handshake failed gets the
    // recovery screen instead of being silently dropped back to the login page.
    const content =
        firebaseUser && authError && !user
            ? <HandshakeErrorScreen onRetry={retryHandshake} onSignOut={logout} busy={retrying} />
            : user && accountStatus !== 'active'
                ? <AccountStatusGate
                      status={accountStatus}
                      deletionScheduledAt={user.deletionScheduledAt}
                      onRestore={restoreAccount}
                      onExport={downloadAccountExport}
                      onSignOut={logout}
                  />
                : children;

    return (
        <UserContext.Provider value={{
            user, workspace, loading, authError, retryHandshake,
            loginGoogle, loginEmail, signupEmail, logout,
            resetPassword, resendVerification,
            // Phase 2: 'active' | 'pending_deletion' | 'suspended'. Consumers
            // should not need it (the gate above already replaces the app), but
            // Settings reads it to keep the Danger Zone honest.
            accountStatus, refreshAccount: runHandshake,
            // Read from the live Firebase user, not the backend copy, so it
            // reflects a verification that happened in another tab.
            emailVerified: firebaseUser?.emailVerified ?? false,
        }}>
            <WorkspaceProvider>
                {!loading && content}
            </WorkspaceProvider>
        </UserContext.Provider>
    );
};
